<template>
  <div class="ver-emision-container container my-5 py-4">
    <!-- Loader inicial -->
    <div
      v-if="loadingHeader"
      class="text-center py-5"
    >
      <font-awesome-icon
        icon="circle-notch"
        class="fa-spin fa-3x text-primary mb-3"
      />
      <p class="lead text-muted">
        Cargando datos de la emisión...
      </p>
    </div>

    <!-- Error de token o acceso bloqueado -->
    <div
      v-else-if="headerError"
      class="card mx-auto p-4 shadow-sm text-center"
      style="max-width: 550px; border-radius: 12px;"
    >
      <div class="my-3 text-danger">
        <font-awesome-icon
          icon="exclamation-triangle"
          class="fa-4x"
        />
      </div>
      <h2 class="h4 font-weight-bold text-dark mb-3">
        Enlace Inválido
      </h2>
      <p class="text-muted">
        {{ headerErrorMessage }}
      </p>
      <router-link
        to="/"
        class="btn btn-primary mt-3 px-4 py-2 font-weight-bold"
        style="border-radius: 20px;"
      >
        Ir al Inicio
      </router-link>
    </div>

    <div
      v-else
      class="emision-content"
    >
      <!-- CABECERA DEL EMISOR -->
      <div
        class="card shadow-sm mb-4 border-0"
        style="border-radius: 12px; overflow: hidden;"
      >
        <div
          class="bg-primary text-white p-3 d-flex align-items-center
                 justify-content-between flex-wrap"
        >
          <div class="d-flex align-items-center">
            <font-awesome-icon
              icon="shield-alt"
              class="fa-2x mr-2"
            />
            <span class="font-weight-bold h6 mb-0">
              DOCUMENTO VERIFICADO EN LÍNEA
            </span>
          </div>
          <span
            class="badge badge-light text-primary font-weight-bold py-2 px-3"
          >
            SISTEMA AMII APP
          </span>
        </div>

        <div class="card-body p-4 bg-light text-center text-md-left">
          <h2 class="h4 font-weight-bold text-dark mb-1">
            {{ issuer.name || 'Emisor Autorizado' }}
          </h2>
          <p
            v-if="issuer.identification"
            class="text-muted small mb-0 font-weight-bold"
          >
            Identificación:
            {{ getIdentificationTypeName(issuer.identificationType) }}
            {{ issuer.identification }}
          </p>
        </div>
      </div>

      <!-- PASO 1: VERIFICACIÓN POR CÉDULA/DOCUMENTO -->
      <div
        v-if="!verified"
        class="card mx-auto p-4 shadow-sm"
        style="max-width: 550px; border-radius: 12px;"
      >
        <div class="text-center mb-4">
          <div
            class="icon-circle bg-light text-primary mx-auto mb-3 p-3
                   rounded-circle d-inline-block"
          >
            <font-awesome-icon
              icon="user-lock"
              class="fa-2x"
            />
          </div>
          <h3 class="h5 font-weight-bold text-dark">
            Verificación del Paciente
          </h3>
          <p class="text-muted small">
            Ingrese el documento de identidad del paciente asignado.
          </p>
          <div
            v-if="patientMasked.maskedName"
            class="alert alert-info py-2 small"
          >
            <strong>Paciente asignado:</strong> {{ patientMasked.maskedName }}
          </div>
        </div>

        <form @submit.prevent="verificarPaciente">
          <div class="form-group mb-3">
            <SelectIdentificationType
              v-model="patientIdentificationType"
              class-name="md-outline outline-select w-100 my-2"
              @save="onIdentificationTypeSelected"
            />
          </div>

          <div class="form-group mb-3">
            <label class="font-weight-bold small">
              Número de Documento / Cédula
            </label>
            <input
              ref="inputPatientIdentification"
              v-model="patientIdentification"
              type="text"
              class="form-control"
              :placeholder="identificationPlaceholder"
              required
              @input="onIdentificationInput"
            >
            <small class="form-text text-muted mt-1">
              {{ identificationAdvice }}
            </small>
          </div>

          <div
            v-if="verifyError"
            class="alert alert-danger small"
          >
            {{ verifyErrorMessage }}
          </div>

          <div
            v-if="attemptsRemaining !== null && attemptsRemaining < 10"
            class="text-right small text-warning font-weight-bold mb-3"
          >
            Intentos restantes: {{ attemptsRemaining }} de 10
          </div>

          <button
            type="submit"
            class="btn btn-primary btn-block font-weight-bold py-2"
            style="border-radius: 20px;"
            :disabled="verifying || blocked"
          >
            <font-awesome-icon
              v-if="verifying"
              icon="circle-notch"
              class="fa-spin mr-1"
            />
            {{ verifying ? 'Verificando...' : 'Ver Documento / Resultados' }}
          </button>
        </form>
      </div>

      <!-- PASO 2: DOCUMENTO / RESULTADOS -->
      <div v-else>
        <div
          class="d-flex justify-content-between align-items-center
                 mb-3 no-print"
        >
          <span class="badge badge-success px-3 py-2">
            <font-awesome-icon icon="check-circle" /> Identidad Verificada
          </span>
          <button
            class="btn btn-outline-primary font-weight-bold"
            style="border-radius: 20px;"
            @click="imprimirDocumento"
          >
            <font-awesome-icon icon="print" /> Imprimir / PDF
          </button>
        </div>

        <!-- VISTA DE DOCUMENTO -->
        <div
          v-if="targetType === 'DOCUMENT' && documentData"
          class="card shadow-sm p-4 bg-white"
          style="border-radius: 12px;"
        >
          <div
            class="border-bottom pb-3 mb-3 d-flex justify-content-between
                   align-items-center flex-wrap"
          >
            <h3 class="h4 font-weight-bold text-dark mb-0">
              {{ documentData.title || 'Documento Médico' }}
            </h3>
            <span
              v-if="documentData.createdAt"
              class="text-muted small"
            >
              <strong>Fecha:</strong> {{ documentData.createdAt }}
            </span>
          </div>

          <div class="bg-light p-3 rounded mb-4">
            <div class="row small">
              <div class="col-md-6 mb-1">
                <strong>Paciente:</strong>
                {{ documentData.patient.fname }}
                {{ documentData.patient.mname }}
                {{ documentData.patient.lname }}
                {{ documentData.patient.llname }}
              </div>
              <div class="col-md-6 mb-1">
                <strong>Documento:</strong>
                {{ getIdentificationTypeName(
                  documentData.patient.identificationType
                ) }}
                {{ documentData.patient.identification }}
              </div>
            </div>
          </div>

          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="documentData.content"
            class="document-html-body my-3"
            v-html="documentData.content"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>

        <!-- VISTA DE RESULTADOS DE LABORATORIO -->
        <div
          v-else-if="
            (targetType === 'LAB_ORDER' || targetType === 'LAB_ORDER_ITEM')
              && labOrderData
          "
          class="card shadow-sm p-4 bg-white"
          style="border-radius: 12px;"
        >
          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="labOrderData.renderedHtml"
            class="rendered-lab-report my-2"
            v-html="labOrderData.renderedHtml"
          />
          <div
            v-else
            class="text-center text-muted py-4"
          >
            No hay resultados validados disponibles para esta orden.
          </div>
          <!-- eslint-enable vue/no-v-html -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import SelectIdentificationType, {identificationTypes}
from '@/components/SelectIdentificationType.vue';
import {validateIdentification} from '@/utils/validations.js';

const PUBLIC_EMITTED_HEADER_QUERY = gql`
  query publicEmittedHeader(
    $ano: String!
    $mes: String!
    $token: String!
  ) {
    publicEmittedHeader(ano: $ano, mes: $mes, token: $token) {
      success
      code
      targetType
      issuer {
        name
        identificationType
        identification
      }
      patient {
        maskedName
        identificationTypeHint
      }
      issuedAt
      attemptsRemaining
      blocked
    }
  }
`;

const PUBLIC_EMITTED_DOCUMENT_QUERY = gql`
  query publicEmittedDocument(
    $ano: String!
    $mes: String!
    $token: String!
    $patientIdentificationType: String!
    $patientIdentification: String!
  ) {
    publicEmittedDocument(
      ano: $ano
      mes: $mes
      token: $token
      patientIdentificationType: $patientIdentificationType
      patientIdentification: $patientIdentification
    ) {
      success
      code
      attemptsRemaining
      blocked
      document {
        id
        title
        content
        createdAt
        doctor {
          name
          identificationType
          identification
        }
        patient {
          fname
          mname
          lname
          llname
          identificationType
          identification
        }
      }
    }
  }
`;

const PUBLIC_EMITTED_LAB_ORDER_QUERY = gql`
  query publicEmittedLabOrder(
    $ano: String!
    $mes: String!
    $token: String!
    $patientIdentificationType: String!
    $patientIdentification: String!
  ) {
    publicEmittedLabOrder(
      ano: $ano
      mes: $mes
      token: $token
      patientIdentificationType: $patientIdentificationType
      patientIdentification: $patientIdentification
    ) {
      success
      code
      attemptsRemaining
      blocked
      labOrder {
        id
        code
        createdAt
        status
        renderedHtml
      }
    }
  }
`;

export default {
  name: 'VerEmisionPublica',
  components: {
    SelectIdentificationType,
  },
  data() {
    return {
      ano: '',
      mes: '',
      token: '',
      loadingHeader: true,
      headerError: false,
      headerErrorMessage: '',
      targetType: '',
      issuer: {},
      patientMasked: {},
      attemptsRemaining: 10,
      blocked: false,
      patientIdentificationType: 'ven_cedula',
      patientIdentification: 'V-',
      verifying: false,
      verified: false,
      verifyError: false,
      verifyErrorMessage: '',
      documentData: null,
      labOrderData: null,
    };
  },
  computed: {
    currentIdentificationTypeObj() {
      const types = SelectIdentificationType.data().identificationTypes;
      return types.find((t) => t.value === this.patientIdentificationType)
        || {
          name: 'Documento',
          value: this.patientIdentificationType,
          regExp: '',
          mensaje: 'Ingrese su número de documento.',
        };
    },
    identificationPlaceholder() {
      switch (this.patientIdentificationType) {
        case 'ven_cedula':
          return 'Ej. V-12345678';
        case 'ury_cedula':
          return 'Ej. 1234567-8';
        case 'ecu_cedula':
          return 'Ej. 1712345678';
        case 'arg_dni':
          return 'Ej. 12345678';
        case 'chl_rut':
          return 'Ej. 12345678-9';
        case 'usa_ssn':
          return 'Ej. 123-45-678';
        case 'pasaporte':
          return 'Ej. A1234567';
        default:
          return 'Ej. 12345678';
      }
    },
    identificationAdvice() {
      return this.currentIdentificationTypeObj.mensaje
        || 'Ingrese su documento según el formato correspondiente.';
    },
  },
  watch: {
    patientIdentificationType(newType) {
      this.handleIdentificationTypeChange(newType);
    },
  },
  created() {
    this.ano = this.$route.params.ano;
    this.mes = this.$route.params.mes;
    this.token = this.$route.params.token;
    this.cargarEncabezado();
  },
  methods: {
    getIdentificationTypeName(type) {
      if (!type) return '';
      return identificationTypes[type] || type;
    },

    onIdentificationTypeSelected(typeObj) {
      if (typeObj && typeObj.value) {
        this.handleIdentificationTypeChange(typeObj.value);
      }
    },

    handleIdentificationTypeChange(newType) {
      if (newType === 'ven_cedula') {
        this.patientIdentification = 'V-';
      } else {
        this.patientIdentification = '';
      }
      this.verifyError = false;
      this.verifyErrorMessage = '';
    },

    onIdentificationInput() {
      this.verifyError = false;
      this.verifyErrorMessage = '';
    },

    getMsgFromCode(code, fallback) {
      switch (code) {
        case 'RATE_LIMIT_BLOCKED':
          return 'Ha superado el límite de 10 intentos fallidos.'
            + ' Por seguridad, el acceso está bloqueado por 15 minutos.';
        case 'INVALID_IDENTIFICATION':
          return 'El tipo o número de documento ingresado'
            + ' no coincide con el registro del paciente.';
        case 'TOKEN_NOT_FOUND':
          return 'El enlace de emisión es inválido o no existe.';
        case 'INTERNAL_ERROR':
          return 'Ocurrió un error en el servidor al consultar la emisión.';
        default:
          return fallback || 'Error procesando la solicitud.';
      }
    },

    async cargarEncabezado() {
      this.loadingHeader = true;
      this.headerError = false;
      try {
        const {data} = await this.$apollo.query({
          query: PUBLIC_EMITTED_HEADER_QUERY,
          variables: {
            ano: this.ano,
            mes: this.mes,
            token: this.token,
          },
          fetchPolicy: 'no-cache',
        });

        const res = data.publicEmittedHeader;
        if (!res || !res.success) {
          this.headerError = true;
          this.headerErrorMessage = this.getMsgFromCode(
              res ? res.code : null,
              'El enlace de emisión es inválido o no existe.',
          );
          return;
        }

        this.targetType = res.targetType;
        this.issuer = res.issuer || {};
        this.patientMasked = res.patient || {};
        this.attemptsRemaining = res.attemptsRemaining !== null
          ? res.attemptsRemaining
          : 10;
        this.blocked = res.blocked || false;

        if (this.patientMasked.identificationTypeHint) {
          this.patientIdentificationType =
            this.patientMasked.identificationTypeHint;
        }
      } catch (err) {
        console.error('Error cargando encabezado:', err);
        this.headerError = true;
        this.headerErrorMessage =
          'No se pudo conectar con el servidor para validar el enlace.';
      } finally {
        this.loadingHeader = false;
      }
    },

    async verificarPaciente() {
      const ident = (this.patientIdentification || '').trim();
      const typeObj = this.currentIdentificationTypeObj;

      if (!typeObj || !typeObj.value
          || !validateIdentification(typeObj, ident)) {
        this.verifyError = true;
        this.verifyErrorMessage = (typeObj && typeObj.mensaje)
          ? typeObj.mensaje
          : 'Por favor ingrese su número de documento de identidad.';
        return;
      }

      this.verifying = true;
      this.verifyError = false;
      this.verifyErrorMessage = '';

      try {
        if (this.targetType === 'DOCUMENT') {
          const {data} = await this.$apollo.query({
            query: PUBLIC_EMITTED_DOCUMENT_QUERY,
            variables: {
              ano: this.ano,
              mes: this.mes,
              token: this.token,
              patientIdentificationType: this.patientIdentificationType,
              patientIdentification: ident,
            },
            fetchPolicy: 'no-cache',
          });

          const res = data.publicEmittedDocument;
          if (res && res.success && res.document) {
            this.verified = true;
            this.documentData = res.document;
          } else {
            this.verifyError = true;
            this.verifyErrorMessage = this.getMsgFromCode(
                res ? res.code : null,
                'La identificación ingresada no es correcta.',
            );
            if (res && res.attemptsRemaining !== undefined) {
              this.attemptsRemaining = res.attemptsRemaining;
            }
            if (res && res.blocked) {
              this.blocked = true;
            }
          }
        } else {
          const {data} = await this.$apollo.query({
            query: PUBLIC_EMITTED_LAB_ORDER_QUERY,
            variables: {
              ano: this.ano,
              mes: this.mes,
              token: this.token,
              patientIdentificationType: this.patientIdentificationType,
              patientIdentification: ident,
            },
            fetchPolicy: 'no-cache',
          });

          const res = data.publicEmittedLabOrder;
          if (res && res.success && res.labOrder) {
            this.verified = true;
            this.labOrderData = res.labOrder;
          } else {
            this.verifyError = true;
            this.verifyErrorMessage = this.getMsgFromCode(
                res ? res.code : null,
                'La identificación ingresada no es correcta.',
            );
            if (res && res.attemptsRemaining !== undefined) {
              this.attemptsRemaining = res.attemptsRemaining;
            }
            if (res && res.blocked) {
              this.blocked = true;
            }
          }
        }
      } catch (err) {
        console.error('Error verificando paciente:', err);
        this.verifyError = true;
        this.verifyErrorMessage =
          'Ocurrió un error al verificar los datos con el servidor.';
      } finally {
        this.verifying = false;
      }
    },

    imprimirDocumento() {
      window.print();
    },
  },
};
</script>

<style scoped>
@media print {
  .no-print {
    display: none !important;
  }
}
.ver-emision-container {
  max-width: 900px;
}
.document-html-body {
  line-height: 1.6;
}
</style>
